/**
 * BOT Product Page v2.0
 * Handles variants, gallery, lightbox, sticky bar, AJAX cart
 */

(function() {
  'use strict';

  class BotProductPage {
    constructor(section) {
      this.section = section;
      this.sectionId = section.dataset.sectionId;
      this.productId = section.dataset.productId;
      this.data = window.botProductData?.[this.sectionId] || {};

      this.variants = this.data.variants || [];
      this.currentVariant = this.variants.find(v => v.id === this.data.currentVariantId) || this.variants[0];

      this.elements = {
        // Media
        mainImage: section.querySelector('[data-main-image]'),
        thumbnails: section.querySelectorAll('[data-thumb]'),

        // Form
        form: section.querySelector('[data-product-form]'),
        variantInput: section.querySelector('[data-variant-id]'),
        qtyInput: section.querySelector('[data-qty-input]'),
        formQty: section.querySelector('[data-form-qty]'),

        // Options
        optionSelects: section.querySelectorAll('[data-option-select]'),
        optionValues: section.querySelectorAll('[data-option-value]'),
        selectedDisplays: section.querySelectorAll('[data-selected]'),

        // Buttons
        addBtn: section.querySelector('[data-add-btn]'),
        btnText: section.querySelector('[data-btn-text]'),
        buyNowBtn: section.querySelector('[data-buy-now]'),

        // Price
        priceWrapper: section.querySelector('[data-price-wrapper]'),
        price: section.querySelector('[data-price]'),
        comparePrice: section.querySelector('[data-compare-price]'),
        savings: section.querySelector('[data-savings]'),

        // Other
        sku: section.querySelector('[data-sku-value]'),
        showMoreBtn: section.querySelector('[data-show-more]'),

        // Sticky
        stickyBar: section.querySelector('[data-sticky-bar]'),
        stickyImg: section.querySelector('[data-sticky-img]'),
        stickyPrice: section.querySelector('[data-sticky-price]'),
        stickyAddBtn: section.querySelector('[data-sticky-add]'),

        // Lightbox
        lightbox: section.querySelector('[data-lightbox]'),
        lightboxImg: section.querySelector('[data-lightbox-img]'),
        lightboxDots: section.querySelectorAll('[data-lightbox-dot]'),

        // Countdown
        countdown: section.querySelector('[data-countdown]'),

        // Copy link
        copyLinkBtn: section.querySelector('[data-copy-link]')
      };

      this.init();
    }

    init() {
      this.bindEvents();
      this.initQuantity();
      this.initLightbox();
      this.initStickyBar();
      this.initCountdown();
      this.initShowMore();
      this.initAccordionShowMore();
    }

    bindEvents() {
      // Thumbnails
      this.elements.thumbnails.forEach(thumb => {
        thumb.addEventListener('click', () => this.onThumbClick(thumb));
      });

      // Option selects (dropdown)
      this.elements.optionSelects.forEach(select => {
        select.addEventListener('change', () => this.onOptionChange());
      });

      // Option values (buttons/swatches)
      this.elements.optionValues.forEach(value => {
        value.addEventListener('click', (e) => this.onValueClick(e, value));
      });

      // Form submit
      if (this.elements.form) {
        this.elements.form.addEventListener('submit', (e) => this.onFormSubmit(e));
      }

      // Buy now
      if (this.elements.buyNowBtn) {
        this.elements.buyNowBtn.addEventListener('click', () => this.onBuyNow());
      }

      // Sticky bar add
      if (this.elements.stickyAddBtn) {
        this.elements.stickyAddBtn.addEventListener('click', () => this.addToCart());
      }

      // Copy link
      if (this.elements.copyLinkBtn) {
        this.elements.copyLinkBtn.addEventListener('click', () => this.copyLink());
      }
    }

    // ========== THUMBNAILS ==========
    onThumbClick(thumb) {
      // Update active state
      this.elements.thumbnails.forEach(t => t.classList.remove('is-active'));
      thumb.classList.add('is-active');

      // Update main image
      if (this.elements.mainImage) {
        const src = thumb.dataset.src;
        const srcset = thumb.dataset.srcset;
        const alt = thumb.dataset.alt;

        this.elements.mainImage.src = src;
        if (srcset) this.elements.mainImage.srcset = srcset;
        if (alt) this.elements.mainImage.alt = alt;
      }

      // Update sticky image
      if (this.elements.stickyImg) {
        this.elements.stickyImg.src = thumb.querySelector('img')?.src || '';
      }
    }

    // ========== VARIANTS ==========
    onValueClick(e, label) {
      const index = parseInt(label.dataset.index);
      const value = label.dataset.value;

      // Update radio
      const radio = label.querySelector('input[type="radio"]');
      if (radio) radio.checked = true;

      // Update visual selection
      const siblings = this.section.querySelectorAll(`[data-option-value][data-index="${index}"]`);
      siblings.forEach(s => s.classList.remove('is-active'));
      label.classList.add('is-active');

      // Update selected display
      const selectedDisplay = this.section.querySelector(`[data-option-index="${index}"] [data-selected]`);
      if (selectedDisplay) selectedDisplay.textContent = value;

      this.onOptionChange();
    }

    onOptionChange() {
      const selectedOptions = this.getSelectedOptions();
      const variant = this.findVariant(selectedOptions);

      if (variant) {
        this.currentVariant = variant;
        this.updateVariantUI(variant);
        this.updateURL(variant);

        // Dispatch event
        document.dispatchEvent(new CustomEvent('variant:changed', {
          detail: { variant, sectionId: this.sectionId }
        }));
      }
    }

    getSelectedOptions() {
      const options = [];

      // From selects
      this.elements.optionSelects.forEach(select => {
        options[parseInt(select.dataset.index)] = select.value;
      });

      // From buttons/swatches
      const checked = this.section.querySelectorAll('[data-option-value] input:checked');
      checked.forEach(input => {
        const label = input.closest('[data-option-value]');
        if (label) {
          options[parseInt(label.dataset.index)] = input.value;
        }
      });

      return options;
    }

    findVariant(options) {
      return this.variants.find(variant => {
        return options.every((opt, i) => variant[`option${i + 1}`] === opt);
      });
    }

    updateVariantUI(variant) {
      // Update variant input
      if (this.elements.variantInput) {
        this.elements.variantInput.value = variant.id;
      }

      // Update price
      if (this.elements.price) {
        this.elements.price.textContent = this.formatMoney(variant.price);
        this.elements.price.classList.toggle('bot-pp__price--sale', variant.compare_at_price > variant.price);
      }

      if (this.elements.comparePrice) {
        if (variant.compare_at_price > variant.price) {
          this.elements.comparePrice.textContent = this.formatMoney(variant.compare_at_price);
          this.elements.comparePrice.style.display = '';
        } else {
          this.elements.comparePrice.style.display = 'none';
        }
      }

      // Update sticky price
      if (this.elements.stickyPrice) {
        this.elements.stickyPrice.textContent = this.formatMoney(variant.price);
      }

      // Update SKU
      if (this.elements.sku && variant.sku) {
        this.elements.sku.textContent = variant.sku;
      }

      // Update button
      this.updateButton(variant);

      // Update image if variant has featured image
      if (variant.featured_image) {
        this.updateMainImage(variant.featured_image);
      }
    }

    updateButton(variant) {
      if (!this.elements.addBtn) return;

      const available = variant.available;
      const isPreorder = this.data.isPreorder;

      // Remove all state classes
      this.elements.addBtn.classList.remove('bot-pp__add-btn--soldout', 'bot-pp__add-btn--preorder');
      this.elements.addBtn.disabled = false;

      if (!available) {
        this.elements.addBtn.classList.add('bot-pp__add-btn--soldout');
        this.elements.addBtn.disabled = true;
        if (this.elements.btnText) {
          this.elements.btnText.textContent = this.data.soldoutText || 'Sold Out';
        }
      } else if (isPreorder) {
        this.elements.addBtn.classList.add('bot-pp__add-btn--preorder');
        if (this.elements.btnText) {
          this.elements.btnText.textContent = this.data.preorderText || 'Pre-Order';
        }
      } else {
        if (this.elements.btnText) {
          this.elements.btnText.textContent = this.data.addText || 'Add to Cart';
        }
      }
    }

    updateMainImage(image) {
      if (!this.elements.mainImage) return;

      const src = image.src.startsWith('http') ? image.src : `https:${image.src}`;
      this.elements.mainImage.src = src.replace(/(\.[^\.]+)$/, '_1000x$1');

      // Update active thumbnail
      this.elements.thumbnails.forEach(thumb => {
        const mediaId = thumb.dataset.mediaId;
        thumb.classList.toggle('is-active', mediaId == image.id);
      });
    }

    updateURL(variant) {
      if (!history.replaceState) return;
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      history.replaceState({}, '', url.toString());
    }

    formatMoney(cents) {
      const amount = (cents / 100).toFixed(2);
      // Try to use Shopify's format, fallback to basic
      if (window.Shopify?.currency?.active) {
        return `${window.Shopify.currency.active} ${amount}`;
      }
      return `$${amount}`;
    }

    // ========== QUANTITY ==========
    initQuantity() {
      const minusBtn = this.section.querySelector('[data-qty-minus]');
      const plusBtn = this.section.querySelector('[data-qty-plus]');

      if (minusBtn) {
        minusBtn.addEventListener('click', () => this.updateQuantity(-1));
      }
      if (plusBtn) {
        plusBtn.addEventListener('click', () => this.updateQuantity(1));
      }
    }

    updateQuantity(delta) {
      if (!this.elements.qtyInput) return;

      let value = parseInt(this.elements.qtyInput.value) || 1;
      value = Math.max(1, value + delta);
      this.elements.qtyInput.value = value;

      if (this.elements.formQty) {
        this.elements.formQty.value = value;
      }
    }

    // ========== FORM SUBMIT (AJAX) ==========
    async onFormSubmit(e) {
      e.preventDefault();

      if (!this.currentVariant?.available) return;

      const btn = this.elements.addBtn;
      btn.classList.add('is-loading');

      try {
        await this.addToCart();
      } finally {
        btn.classList.remove('is-loading');
      }
    }

    async addToCart() {
      const quantity = parseInt(this.elements.qtyInput?.value) || 1;

      const formData = {
        items: [{
          id: this.currentVariant.id,
          quantity: quantity
        }]
      };

      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify(formData)
        });

        if (!response.ok) {
          throw new Error('Add to cart failed');
        }

        const data = await response.json();

        // Dispatch event
        document.dispatchEvent(new CustomEvent('cart:item-added', {
          detail: { items: data.items }
        }));
        document.dispatchEvent(new CustomEvent('cart:updated'));

        // Update cart count
        if (window.botUpdateCartCount) window.botUpdateCartCount();

        // Try to open cart drawer, fallback to popup
        const drawerOpened = window.botOpenCartDrawer ? window.botOpenCartDrawer() : false;
        if (!drawerOpened && this.data.popup?.enabled && window.botShowCartPopup) {
          const item = data.items ? data.items[0] : data;
          // Fallback image/title from product data if API response is missing them
          if (!item.image && this.data.productImage) item.image = this.data.productImage;
          if (!item.product_title && this.data.productTitle) item.product_title = this.data.productTitle;
          window.botShowCartPopup(item, this.data.popup.configName || 'default');
        }

      } catch (error) {
        // Silent fail
      }
    }

    // ========== BUY NOW ==========
    onBuyNow() {
      if (!this.currentVariant?.available) return;

      const quantity = parseInt(this.elements.qtyInput?.value) || 1;
      window.location.href = `/cart/${this.currentVariant.id}:${quantity}`;
    }

    // ========== LIGHTBOX ==========
    initLightbox() {
      if (!this.elements.lightbox) return;

      this.lightboxIndex = 0;
      this.lightboxImages = Array.from(this.elements.lightboxDots).map(dot => dot.dataset.src);

      // Open buttons
      this.section.querySelectorAll('[data-lightbox-open]').forEach(btn => {
        btn.addEventListener('click', () => this.openLightbox());
      });

      // Close
      this.section.querySelector('[data-lightbox-close]')?.addEventListener('click', () => this.closeLightbox());

      // Navigation
      this.section.querySelector('[data-lightbox-prev]')?.addEventListener('click', () => this.lightboxNav(-1));
      this.section.querySelector('[data-lightbox-next]')?.addEventListener('click', () => this.lightboxNav(1));

      // Dots
      this.elements.lightboxDots.forEach((dot, i) => {
        dot.addEventListener('click', () => this.lightboxGoTo(i));
      });

      // Close on backdrop click
      this.elements.lightbox.addEventListener('click', (e) => {
        if (e.target === this.elements.lightbox) this.closeLightbox();
      });

      // Close on Escape
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.elements.lightbox.classList.contains('is-open')) {
          this.closeLightbox();
        }
      });
    }

    openLightbox() {
      this.elements.lightbox.classList.add('is-open');
      this.elements.lightbox.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      this.lightboxGoTo(0);
    }

    closeLightbox() {
      this.elements.lightbox.classList.remove('is-open');
      this.elements.lightbox.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
    }

    lightboxNav(delta) {
      let newIndex = this.lightboxIndex + delta;
      if (newIndex < 0) newIndex = this.lightboxImages.length - 1;
      if (newIndex >= this.lightboxImages.length) newIndex = 0;
      this.lightboxGoTo(newIndex);
    }

    lightboxGoTo(index) {
      this.lightboxIndex = index;
      if (this.elements.lightboxImg && this.lightboxImages[index]) {
        this.elements.lightboxImg.src = this.lightboxImages[index];
      }
      this.elements.lightboxDots.forEach((dot, i) => {
        dot.classList.toggle('is-active', i === index);
      });
    }

    // ========== STICKY BAR ==========
    initStickyBar() {
      if (!this.elements.stickyBar) return;

      const form = this.elements.form;
      if (!form) return;

      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          this.elements.stickyBar.classList.toggle('is-visible', !entry.isIntersecting);
        });
      }, { threshold: 0, rootMargin: '-100px 0px 0px 0px' });

      observer.observe(form);
    }

    // ========== COUNTDOWN ==========
    initCountdown() {
      if (!this.elements.countdown) return;

      const countdown = this.elements.countdown;
      const endDate = new Date(countdown.dataset.end);
      if (isNaN(endDate)) return;

      const daysEl = countdown.querySelector('[data-days]');
      const hoursEl = countdown.querySelector('[data-hours]');
      const minsEl = countdown.querySelector('[data-mins]');
      const secsEl = countdown.querySelector('[data-secs]');
      const timerEl = countdown.querySelector('[data-timer]');
      const titleEl = countdown.querySelector('.bot-pp__countdown-title');

      const expiredText = countdown.dataset.expiredText || 'Offer has ended';
      const hideExpired = countdown.dataset.hideExpired === 'true';

      let intervalId = null;

      const update = () => {
        const now = new Date();
        let diff = Math.max(0, endDate - now) / 1000;

        // Check if expired
        if (diff === 0) {
          if (hideExpired) {
            countdown.classList.add('is-hidden');
          } else {
            countdown.classList.add('bot-pp__countdown--expired');
            if (timerEl) timerEl.style.display = 'none';
            if (titleEl) titleEl.textContent = expiredText;
          }
          if (intervalId) clearInterval(intervalId);
          return;
        }

        const days = Math.floor(diff / 86400);
        diff -= days * 86400;
        const hours = Math.floor(diff / 3600);
        diff -= hours * 3600;
        const mins = Math.floor(diff / 60);
        const secs = Math.floor(diff % 60);

        if (daysEl) daysEl.textContent = String(days).padStart(2, '0');
        if (hoursEl) hoursEl.textContent = String(hours).padStart(2, '0');
        if (minsEl) minsEl.textContent = String(mins).padStart(2, '0');
        if (secsEl) secsEl.textContent = String(secs).padStart(2, '0');
      };

      update();
      intervalId = setInterval(update, 1000);
    }

    // ========== SHOW MORE ==========
    initShowMore() {
      if (!this.elements.showMoreBtn) return;

      const content = this.section.querySelector('[data-collapsible]');
      const moreText = this.elements.showMoreBtn.querySelector('[data-more-text]');
      const lessText = this.elements.showMoreBtn.querySelector('[data-less-text]');

      this.elements.showMoreBtn.addEventListener('click', () => {
        const isExpanded = content.classList.toggle('is-expanded');
        if (moreText) moreText.style.display = isExpanded ? 'none' : '';
        if (lessText) lessText.style.display = isExpanded ? '' : 'none';
      });
    }

    // ========== ACCORDION SHOW MORE ==========
    initAccordionShowMore() {
      this.section.querySelectorAll('[data-acc-show-more]').forEach(btn => {
        const accordion = btn.closest('.bot-pp__accordion, .bot-pp__collapsible-block');
        if (!accordion) return;
        const body = accordion.querySelector('[data-acc-collapsible]');
        const moreText = btn.querySelector('[data-more-text]');
        const lessText = btn.querySelector('[data-less-text]');
        if (!body) return;

        btn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          const isExpanded = body.classList.toggle('is-expanded');
          if (moreText) moreText.style.display = isExpanded ? 'none' : '';
          if (lessText) lessText.style.display = isExpanded ? '' : 'none';
        });
      });
    }

    // ========== COPY LINK ==========
    copyLink() {
      const btn = this.elements.copyLinkBtn;
      if (!btn) return;

      const url = btn.dataset.url || window.location.href;
      const shareBlock = btn.closest('.bot-pp__share');
      const copiedText = shareBlock?.dataset.copiedText || 'Link copied!';

      navigator.clipboard.writeText(url).then(() => {
        const originalHTML = btn.innerHTML;

        // Show copied state
        btn.classList.add('is-copied');
        btn.dataset.copied = copiedText;
        btn.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="20 6 9 17 4 12"/></svg>';

        setTimeout(() => {
          btn.classList.remove('is-copied');
          btn.innerHTML = originalHTML;
        }, 2000);
      }).catch(() => {
        // Fallback for older browsers
        const textArea = document.createElement('textarea');
        textArea.value = url;
        textArea.style.position = 'fixed';
        textArea.style.opacity = '0';
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);

        btn.classList.add('is-copied');
        btn.dataset.copied = copiedText;
        setTimeout(() => btn.classList.remove('is-copied'), 2000);
      });
    }
  }

  // Initialize all sections
  function init() {
    document.querySelectorAll('.bot-pp').forEach(section => {
      if (!section.dataset.initialized) {
        new BotProductPage(section);
        section.dataset.initialized = 'true';
      }
    });
  }

  // Run on DOMContentLoaded or immediately if already loaded
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-init on section reload (Shopify Theme Editor)
  document.addEventListener('shopify:section:load', (e) => {
    const section = e.target.querySelector('.bot-pp');
    if (section) {
      section.removeAttribute('data-initialized');
      new BotProductPage(section);
      section.dataset.initialized = 'true';
    }
  });

})();
